import { Box } from "styles/globalComponents";
import { Button } from "Components/Button";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { createToast } from "Utils/toastFunc";
import {
  getPartnersDatesAPI,
  getPartnersHoursAPI,
  reschedulePartperAPI,
} from "Requests/SchedulePartners";
import Modal from "react-modal";
import { useEffect, useState } from "react";
import { MonthsOptions } from "Components/MonthsOptions";
import { Loader } from "Components/Loader";
import { DateTime } from "Components/Cards/DateTime";
import { ButtonsHolder, SelectScheduleContainer } from "../styles";

interface RescheduleModalProps {
  isOpen: boolean;
  requestClose: () => void;
  refetch: () => void;
  acuityId: number;
  cep: string;
  appointmentTypeID: number;
}

export const RescheduleModal = ({
  isOpen,
  refetch,
  requestClose,
  acuityId,
  cep,
  appointmentTypeID,
}: RescheduleModalProps) => {
  const [dateRequest, setDateRequest] = useState(() => {
    const currentDate = new Date();

    const monthFormatted = String(currentDate.getMonth() + 1).padStart(2, "0");

    return `${currentDate.getFullYear()}-${monthFormatted}`;
  });

  const [appointment, setAppointment] = useState({
    query_date: "",
    query_hour: "",
  });

  const availableDates = useQuery({
    queryKey: ["dates", dateRequest],
    queryFn: () => getPartnersDatesAPI(cep, dateRequest, appointmentTypeID),
    refetchOnWindowFocus: false,
    enabled: !!cep && isOpen,
    retry: 1,
    select: (data) => {
      const arr: any = [];
      data.forEach((date) => {
        const newDate = new Date(`${date.date}T03:00:00`);
        arr.push({
          date: newDate.toLocaleDateString("pt-BR", {
            day: "2-digit",
            month: "2-digit",
          }),
          day: newDate.toLocaleDateString("pt-BR", { weekday: "long" }),
          query: date.date,
        });
      });

      return arr;
    },
  });

  const availableHours = useQuery({
    queryKey: ["hours", dateRequest, appointment.query_date],
    queryFn: () =>
      getPartnersHoursAPI(cep, appointment.query_date, appointmentTypeID),
    refetchOnWindowFocus: false,
    enabled: !!appointment.query_date,
  });

  const client = useQueryClient();

  const handleSelectCalendar = (query: { option: string }) => {
    setAppointment((prev) => ({ ...prev, query_date: query.option }));
    setAppointment((prev) => ({ ...prev, query_hour: "" }));
  };

  const resetData = () => {
    setAppointment({
      query_date: "",
      query_hour: "",
    });
    client.removeQueries({ queryKey: ["dates"] });
    client.removeQueries({ queryKey: ["hours"] });
  };

  const handleSelectHour = async (query: { option: string }) => {
    setAppointment((prev) => ({ ...prev, query_hour: query.option }));
  };

  const rescheduleAppointmentMutation = useMutation({
    mutationFn: reschedulePartperAPI,
  });

  const createReschedule = async () => {
    try {
      if (!appointment.query_hour) {
        throw new Error("Horário não selecionado");
      }

      await rescheduleAppointmentMutation.mutateAsync({
        appointment: appointment.query_hour,
        id: acuityId,
      });
      refetch();
      createToast("success", "Reagendamento realizado com sucesso");
      requestClose();
    } catch (errors: any) {
      const error = errors?.response?.data?.errors[0].message;

      if (error) {
        createToast("error", error);
        return;
      }

      if (errors?.message) {
        createToast("error", errors.message);
      }
    }
  };

  const setAppointmentInfo = (data: any) => {
    setAppointment((prev) => ({ ...prev, ...data }));
  };

  const { data } = availableDates ?? {};

  const [pos] = data ?? [];

  const [year, month, day] = pos?.query.split("-") ?? "";
  const currentMonth = new Date(year, month - 1, day);

  const monthSelected = currentMonth.toLocaleDateString("pt-BR", {
    month: "long",
  });

  const capitilizedMonth =
    monthSelected.charAt(0).toUpperCase() + monthSelected.slice(1);

  const error = availableDates?.error as any;

  useEffect(() => {
    return () => resetData();
  }, []);

  return (
    <Modal
      shouldCloseOnOverlayClick={false}
      isOpen={isOpen}
      onRequestClose={requestClose}
    >
      <SelectScheduleContainer>
        <h2 className="text-center text-centered text-purple section-title">
          Selecione data e horário para o reagendamento.
        </h2>

        <div className="schedule-sec">
          <div className="available-months">
            <p className="text-center text-purple schedule-searching text-md">
              Pŕoximos meses
            </p>
            <MonthsOptions
              selectedMonth={dateRequest}
              setRequestMonth={setDateRequest}
              blockedClick={!availableDates.isSuccess}
              setAppointmentInfo={setAppointmentInfo}
            />
          </div>

          {availableDates?.data?.length === 0 && (
            <div className="available-months">
              <p className="text-center text-purple schedule-searching text-md">
                Ainda não há data disponível para o mês selecionado
              </p>
            </div>
          )}

          {availableDates.isError && (
            <p className="text-center text-centered text-purple schedule-searching text-md error">
              {" "}
              {error?.response?.data?.error as string}
            </p>
          )}

          {availableDates.isLoading ? (
            <Box display="flex" justify="center" mt="40">
              <Loader />
            </Box>
          ) : null}

          {availableDates?.data?.length >= 1 && (
            <div className="datetime-result-list">
              <p className="text-center text-purple schedule-searching text-md">
                Datas disponíveis em {capitilizedMonth}
              </p>

              <div className="container-dates-options">
                <div className="dates-list">
                  {availableDates?.data?.map((date: any) => (
                    <DateTime
                      dateTimeInfo={date}
                      key={date.date}
                      onSelectDate={handleSelectCalendar}
                      selectedDateTime={appointment.query_date}
                    />
                  ))}
                </div>
              </div>

              {availableHours.isLoading ? (
                <Box display="flex" justify="center" mt="40">
                  <Loader />
                </Box>
              ) : null}

              {availableHours?.data?.length > 1 && (
                <div className="container-times-options">
                  <p className="text-center text-purple schedule-searching text-md">
                    Horários disponíveis
                  </p>
                  <div className="dates-list">
                    {availableHours?.data?.map((time: any) => (
                      <DateTime
                        dateTimeInfo={time}
                        key={time.time}
                        onSelectDate={handleSelectHour}
                        selectedDateTime={appointment.query_hour}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </SelectScheduleContainer>

      <ButtonsHolder>
        {appointment.query_hour && availableHours?.data?.length && (
          <Button
            width="full"
            minHeight="3.7em"
            fontWeight="600"
            onClick={createReschedule}
            loading={rescheduleAppointmentMutation.isLoading}
            disabled={rescheduleAppointmentMutation.isLoading}
          >
            Prosseguir com o reagendamento
          </Button>
        )}
        <Button
          width="full"
          outlined
          minHeight="3.7em"
          fontWeight="600"
          disabled={rescheduleAppointmentMutation.isLoading}
          onClick={requestClose}
        >
          Voltar para detalhes
        </Button>
      </ButtonsHolder>
    </Modal>
  );
};
